import { useEffect, useState, useRef } from "react";
import { Save, Upload, Plus, X, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { fetchContent, adminUpdateContent, adminUploadImage, resolveImageUrl } from "@/lib/api";
import { invalidateContent } from "@/hooks/useSiteContent";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const Field = ({ label, children, hint }) => (
  <label className="block">
    <div className="text-[10px] uppercase tracking-widest text-white/70 font-bold mb-1.5">{label}</div>
    {children}
    {hint && <div className="text-[10px] text-white/40 mt-1">{hint}</div>}
  </label>
);

const TextField = ({ value, onChange, ...rest }) => (
  <Input
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-10 text-white text-sm"
    {...rest}
  />
);

const TextAreaField = ({ value, onChange, rows = 4 }) => (
  <Textarea
    rows={rows}
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    className="bg-[#0A0A0A] border-2 border-[#333] rounded-none text-white text-sm"
  />
);

const ImageField = ({ value, onChange, token }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const data = await adminUploadImage(file, token);
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <div className="flex items-start gap-3">
      <div className="w-20 h-20 bg-black border-2 border-[#333] overflow-hidden flex-shrink-0">
        {value ? (
          <img src={resolveImageUrl(value)} alt="preview" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/30">
            <ImagePlus className="w-6 h-6" />
          </div>
        )}
      </div>
      <div className="flex-1 space-y-1.5">
        <input ref={ref} type="file" accept="image/*" onChange={onPick} className="hidden" />
        <button
          type="button"
          onClick={() => ref.current?.click()}
          disabled={busy}
          className="inline-flex items-center gap-2 bg-[#0A0A0A] border-2 border-[#333] hover:border-[#E60000] text-white px-3 h-9 uppercase tracking-widest text-[10px] font-bold disabled:opacity-50"
        >
          <Upload className="w-3.5 h-3.5" /> {busy ? "Uploading…" : "Upload"}
        </button>
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Or paste an image URL"
          className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-9 text-white text-xs"
        />
      </div>
    </div>
  );
};

export const AdminSiteContent = ({ token }) => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchContent().then(setData).catch(() => toast.error("Couldn't load site content"));
  }, []);

  if (!data) {
    return <div className="text-center py-20 text-white/50 uppercase tracking-widest">Loading…</div>;
  }

  const update = (path) => (val) => {
    setData((d) => {
      const clone = JSON.parse(JSON.stringify(d));
      const keys = path.split(".");
      let cur = clone;
      for (let i = 0; i < keys.length - 1; i++) {
        cur[keys[i]] = cur[keys[i]] || {};
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = val;
      return clone;
    });
  };

  const updateMarqueeItem = (idx, val) => {
    setData((d) => {
      const list = [...(d.hero?.marquee || [])];
      list[idx] = val;
      return { ...d, hero: { ...d.hero, marquee: list } };
    });
  };
  const addMarquee = () => {
    setData((d) => ({ ...d, hero: { ...d.hero, marquee: [...(d.hero?.marquee || []), "New Item"] } }));
  };
  const removeMarquee = (idx) => {
    setData((d) => {
      const list = [...(d.hero?.marquee || [])];
      list.splice(idx, 1);
      return { ...d, hero: { ...d.hero, marquee: list } };
    });
  };

  const updateLocation = (idx, field, val) => {
    setData((d) => {
      const list = [...(d.locations || [])];
      list[idx] = { ...list[idx], [field]: val };
      return { ...d, locations: list };
    });
  };
  const updateHourLine = (locIdx, lineIdx, val) => {
    setData((d) => {
      const locs = [...(d.locations || [])];
      const hrs = [...(locs[locIdx].hours_lines || [])];
      hrs[lineIdx] = val;
      locs[locIdx] = { ...locs[locIdx], hours_lines: hrs };
      return { ...d, locations: locs };
    });
  };
  const addHourLine = (locIdx) => {
    setData((d) => {
      const locs = [...(d.locations || [])];
      const hrs = [...(locs[locIdx].hours_lines || []), ""];
      locs[locIdx] = { ...locs[locIdx], hours_lines: hrs };
      return { ...d, locations: locs };
    });
  };
  const removeHourLine = (locIdx, lineIdx) => {
    setData((d) => {
      const locs = [...(d.locations || [])];
      const hrs = [...(locs[locIdx].hours_lines || [])];
      hrs.splice(lineIdx, 1);
      locs[locIdx] = { ...locs[locIdx], hours_lines: hrs };
      return { ...d, locations: locs };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminUpdateContent(data, token);
      invalidateContent();
      toast.success("Site content saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-site-content" className="space-y-10 pb-24">
      {/* Sticky save bar */}
      <div className="sticky top-0 z-20 -mx-4 sm:mx-0 bg-black border-y-2 border-[#E60000] py-3 px-4 sm:px-6 flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold">Site Content Editor</div>
        <button
          data-testid="content-save-btn"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF3333] text-white px-5 h-10 uppercase tracking-widest text-xs font-bold border-2 border-transparent hover:border-white disabled:opacity-50"
        >
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save All Changes"}
        </button>
      </div>

      {/* HERO */}
      <Section title="Hero (Home page top section)">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Eyebrow / badge text">
            <TextField value={data.hero?.eyebrow} onChange={update("hero.eyebrow")} />
          </Field>
          <Field label="Background photo">
            <ImageField value={data.hero?.image_url} onChange={update("hero.image_url")} token={token} />
          </Field>
          <Field label="Headline line 1">
            <TextField value={data.hero?.title_line1} onChange={update("hero.title_line1")} />
          </Field>
          <Field label="Headline line 2">
            <TextField value={data.hero?.title_line2} onChange={update("hero.title_line2")} />
          </Field>
          <Field label="Headline accent word (red)">
            <TextField value={data.hero?.title_accent} onChange={update("hero.title_accent")} />
          </Field>
          <Field label="Tagline (sub-paragraph)">
            <TextAreaField value={data.hero?.tagline} onChange={update("hero.tagline")} rows={4} />
          </Field>
        </div>

        <div className="mt-5">
          <div className="text-[10px] uppercase tracking-widest text-white/70 font-bold mb-3">Stats (3 cells under tagline)</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((n) => (
              <div key={n} className="border-2 border-[#222] p-3 space-y-2">
                <Field label={`Stat ${n} — number`}>
                  <TextField value={data.hero?.[`stat${n}_number`]} onChange={update(`hero.stat${n}_number`)} />
                </Field>
                <Field label={`Stat ${n} — label`}>
                  <TextField value={data.hero?.[`stat${n}_label`]} onChange={update(`hero.stat${n}_label`)} />
                </Field>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-widest text-white/70 font-bold">Scrolling marquee at bottom of hero</div>
            <button onClick={addMarquee} className="inline-flex items-center gap-1 bg-[#0A0A0A] border-2 border-[#333] hover:border-[#E60000] text-white px-3 h-8 uppercase tracking-widest text-[10px] font-bold">
              <Plus className="w-3 h-3" /> Add
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-2">
            {(data.hero?.marquee || []).map((m, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Input
                  value={m}
                  onChange={(e) => updateMarqueeItem(idx, e.target.value)}
                  className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-9 text-white text-sm flex-1"
                />
                <button onClick={() => removeMarquee(idx)} className="w-9 h-9 flex items-center justify-center bg-[#0A0A0A] border-2 border-[#333] hover:bg-[#E60000] hover:border-[#E60000] text-white/70 hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ABOUT */}
      <Section title="About Section">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Eyebrow"><TextField value={data.about?.eyebrow} onChange={update("about.eyebrow")} /></Field>
          <Field label="Family photo"><ImageField value={data.about?.family_image_url} onChange={update("about.family_image_url")} token={token} /></Field>
          <Field label="Headline line 1"><TextField value={data.about?.title_line1} onChange={update("about.title_line1")} /></Field>
          <Field label="Headline accent (red)"><TextField value={data.about?.title_accent} onChange={update("about.title_accent")} /></Field>
        </div>
        <Field label="Story paragraph 1">
          <TextAreaField value={data.about?.story_paragraph_1} onChange={update("about.story_paragraph_1")} rows={4} />
        </Field>
        <Field label="Story paragraph 2">
          <TextAreaField value={data.about?.story_paragraph_2} onChange={update("about.story_paragraph_2")} rows={4} />
        </Field>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Field label="Quote"><TextAreaField value={data.about?.quote} onChange={update("about.quote")} rows={3} /></Field>
          </div>
          <Field label="Quote author"><TextField value={data.about?.quote_author} onChange={update("about.quote_author")} /></Field>
        </div>
        <Field label="Family blurb (Cole Squad)">
          <TextAreaField value={data.about?.family_blurb} onChange={update("about.family_blurb")} rows={4} />
        </Field>
      </Section>

      {/* LOCATIONS */}
      <Section title="Locations · Hours · Phone · Address">
        {(data.locations || []).map((loc, idx) => (
          <div key={loc.id || idx} className="border-2 border-[#222] p-4 mb-4">
            <div className="text-xs uppercase tracking-widest text-[#E60000] font-bold mb-3">{loc.name || `Location ${idx + 1}`}</div>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Display name"><TextField value={loc.name} onChange={(v) => updateLocation(idx, "name", v)} /></Field>
              <Field label="Phone"><TextField value={loc.phone} onChange={(v) => updateLocation(idx, "phone", v)} /></Field>
              <Field label="Address line 1"><TextField value={loc.address_line1} onChange={(v) => updateLocation(idx, "address_line1", v)} /></Field>
              <Field label="Address line 2 (city/state/zip)"><TextField value={loc.address_line2} onChange={(v) => updateLocation(idx, "address_line2", v)} /></Field>
              <div className="md:col-span-2">
                <Field label="Google Maps URL" hint="Used by 'Get directions' link">
                  <TextField value={loc.map_url} onChange={(v) => updateLocation(idx, "map_url", v)} />
                </Field>
              </div>
            </div>
            <div className="mt-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest text-white/70 font-bold">Hours (one line per row)</div>
                <button onClick={() => addHourLine(idx)} className="inline-flex items-center gap-1 bg-[#0A0A0A] border-2 border-[#333] hover:border-[#E60000] text-white px-3 h-7 uppercase tracking-widest text-[10px] font-bold">
                  <Plus className="w-3 h-3" /> Add
                </button>
              </div>
              <div className="space-y-2">
                {(loc.hours_lines || []).map((line, j) => (
                  <div key={j} className="flex items-center gap-2">
                    <Input value={line} onChange={(e) => updateHourLine(idx, j, e.target.value)} className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-9 text-white text-sm flex-1 font-mono" />
                    <button onClick={() => removeHourLine(idx, j)} className="w-9 h-9 flex items-center justify-center bg-[#0A0A0A] border-2 border-[#333] hover:bg-[#E60000] hover:border-[#E60000] text-white/70 hover:text-white">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </Section>

      {/* SOCIAL */}
      <Section title="Social Media Links">
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Facebook URL"><TextField value={data.social?.facebook} onChange={update("social.facebook")} /></Field>
          <Field label="Instagram URL"><TextField value={data.social?.instagram} onChange={update("social.instagram")} /></Field>
          <Field label="TikTok URL"><TextField value={data.social?.tiktok} onChange={update("social.tiktok")} /></Field>
        </div>
      </Section>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF3333] text-white px-6 h-12 uppercase tracking-widest text-xs font-bold border-2 border-transparent hover:border-white disabled:opacity-50"
        >
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save All Changes"}
        </button>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <section className="border-2 border-[#222] bg-[#0F0F0F]">
    <div className="bg-black border-b-2 border-[#333] px-5 py-3">
      <h3 className="font-heading text-2xl text-white">{title}</h3>
    </div>
    <div className="p-5 space-y-4">{children}</div>
  </section>
);
